import React, { useState, useEffect, useRef } from 'react';
import Moment from 'react-moment';
import { ROLE, STATUS_APPLICATION_WAGON } from 'helpers/constants';
import { ApplicationWagonService } from 'services';
import {
  useWebSocketApplicationWagons,
  useGetApplicationWagons,
  useEditApplication,
  useIsRole,
  useStore,
  queryForApi,
} from 'hooks';
import Menu from 'components/menu';
import Table from 'components/table';
import ActionTable from 'components/table/actions';
import Status from 'components/status';
import Modal from 'components/modal';
import Application from 'components/modal/order/wagon';
import { Input } from 'components/ui/form';
import { Filter, Actions } from 'components/applications';
import styles from '../applications.module.scss';

const scrollMainToTop = () => {
  const main = document.querySelector('main');
  if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
};

const ApplicationWagonPage = ({
  history,
  location: { pathname, search },
  match: {
    params: { userId },
  },
}) => {
  const { setUser, setLoading, setReload } = useStore();
  const isAdminOrManager = useIsRole([ROLE.ADMIN, ROLE.MANAGER]);
  const [
    data,
    filters,
    query,
    setData,
    setQueryAndResetPage,
    totalCount,
    page,
    limit,
    setPage,
    setLimit,
    resetFilters,
  ] = useGetApplicationWagons(userId, true);
  const [edit] = useEditApplication(data, search);
  const [selected, setSelected] = useState([]);
  const [selectAllInFilter, setSelectAllInFilter] = useState(false);
  const isProfile = isAdminOrManager && Number(userId) >= 0;
  const isFirstRender = useRef(true);

  useWebSocketApplicationWagons(userId, data, query, setData);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    scrollMainToTop();
  }, [query]);

  const onExport = async () => {
    setLoading(true);
    const body = selectAllInFilter
      ? { exportAllInFilter: true, userId, ...queryForApi(query) }
      : {
        selected: selected.filter(id => data.some(({ id: _id }) => _id === id)),
        userId,
      };
    const blob = await new ApplicationWagonService({ setUser }).exportApplications(body);
    setLoading(false);

    if (blob) {
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = href;
      link.download = 'applications.xml';
      link.click();
    }
  };

  const onImport = async (e) => {
    const { target: { form, files } } = e;

    if (files.length) {
      const file = files[0];
      const formData = new FormData();
      formData.append('file', file, 'file.xml');
      if (userId) {
        formData.append('userId', userId);
      }

      setLoading(true);
      await new ApplicationWagonService({ setUser }).importApplications(formData);
      setLoading(false);

      setReload();
    }

    form.reset();
  };

  const onCloseModal = (isReload = false) => {
    history.push({ pathname });

    if (isReload) {
      setReload();
    }
  };

  const onSelect = ({ target: { value, checked } }) => {
    const v = Number(value);

    if (selectAllInFilter) {
      setSelectAllInFilter(false);
      const allIds = data.map(({ id }) => id);
      const result = checked
        ? allIds
        : allIds.filter(id => id !== v);
      setSelected(result);
      return;
    }

    let result = [...selected];

    if (checked) {
      if (!result.includes(v)) {
        result.push(v);
      }
    } else {
      result = result.filter(id => id !== v);
    }

    setSelected(result);
  };

  const isSelectedAll = selectAllInFilter
    || (data.length > 0 && data.every(({ id }) => selected.includes(id)));

  const onSelectAll = () => {
    if (isSelectedAll) {
      setSelectAllInFilter(false);
      setSelected([]);
    } else {
      setSelectAllInFilter(true);
      setSelected([]);
    }
  };

  const cells = [...(isAdminOrManager ? [{
    keyName: 'selectAll',
    onRenderName: () => (
      <input
        type="checkbox"
        checked={isSelectedAll}
        onChange={onSelectAll}
      />
    ),
    onRender: ({ id }) => (
      <input
        type="checkbox"
        value={id}
        checked={selectAllInFilter || selected.includes(id)}
        onChange={onSelect}
      />
    ),
  }] : []), {
    name: 'Номер',
    nowrap: true,
    renderField: 'id',
  }, {
    name: 'Дата',
    renderField: 'date',
    nowrap: true,
    onRender: (item, value) => <Moment>{value}</Moment>,
  }, {
    name: 'Грузополучатель',
    onRender: item => item?.userConsignee?.consignee?.name || 'Прочие (9999)',
  }, {
    name: 'Группа',
    renderField: 'productGroup',
  }, {
    name: 'Кол-во тонн',
    renderField: 'ton',
    nowrap: true,
  }, {
    name: 'Станция назначения',
    wrapText: true,
    renderField: 'station.name',
  }, {
    name: 'Статус',
    renderField: 'status',
    nowrap: true,
    onRender: (item, value) => (
      <Status
        value={value}
        status={STATUS_APPLICATION_WAGON}
      />
    ),
  }, {
    keyName: 'actions',
    right: true,
    nowrap: true,
    onRender: ({ id }) => (
      <ActionTable
        data={[{
          name: 'edit',
          title: 'Редактировать',
          onClick: () => {
            history.push({
              pathname,
              search: `?id=${id}`,
            });
          },
        }]}
      />
    ),
  }];

  return (
    <>
      {isProfile && (
        <div className={styles.menu}>
          <Menu />
        </div>
      )}
      {isAdminOrManager ? (
        <Actions
          onExport={onExport}
          onImport={onImport}
        />
      ) : null}
      <div className={styles.search}>
        <Input
          type="text"
          value={query.search || ''}
          onChange={e => setQueryAndResetPage({ search: e.target.value })}
          placeholder="Поиск по заявке, направлению, грузополучателю, станции, договору"
        />
      </div>
      <Filter
        filters={filters}
        query={query}
        setQuery={setQueryAndResetPage}
        showFilter
        onResetFilters={resetFilters}
      />
      <Table
        data={data}
        cells={cells}
        count={totalCount}
        page={page}
        limit={limit}
        onPageChange={({ selected: selectedPage }) => setPage(selectedPage)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(0);
        }}
      />
      {edit ? (
        <Modal closeBackground={false} closeEsc onClose={onCloseModal}>
          <Application edit={edit} onClose={onCloseModal} />
        </Modal>
      ) : null}
    </>
  );
};

export default ApplicationWagonPage;
